/**
 * CameraConfigForm Component
 * Form for configuring multiple cameras (up to 10)
 * Used by Multi-Camera System job type
 */

import React, { useState } from "react";
import { Button, Input } from "../common";
import { CameraConfig } from "../../types";

interface CameraConfigFormProps {
  onConfigChange: (cameras: CameraConfig[]) => void;
  maxCameras?: number;
}

export const CameraConfigForm: React.FC<CameraConfigFormProps> = ({
  onConfigChange,
  maxCameras = 10,
}) => {
  const [cameras, setCameras] = useState<CameraConfig[]>([
    { id: "cam_1", ip: "", location: "", description: "" },
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Add a new camera
   */
  const handleAddCamera = () => {
    if (cameras.length >= maxCameras) return;

    const newCamera: CameraConfig = {
      id: `cam_${cameras.length + 1}`,
      ip: "",
      location: "",
      description: "",
    };

    const updatedCameras = [...cameras, newCamera];
    setCameras(updatedCameras);
    onConfigChange(updatedCameras);
  };

  /**
   * Remove a camera
   */
  const handleRemoveCamera = (index: number) => {
    if (cameras.length <= 1) return;

    const updatedCameras = cameras.filter((_, i) => i !== index);
    // Re-index camera IDs
    const reindexedCameras = updatedCameras.map((cam, i) => ({
      ...cam,
      id: `cam_${i + 1}`,
    }));

    setCameras(reindexedCameras);
    onConfigChange(reindexedCameras);

    // Clear errors for removed camera
    const newErrors = { ...errors };
    delete newErrors[`${index}-ip`];
    delete newErrors[`${index}-location`];
    setErrors(newErrors);
  };

  /**
   * Update a camera field
   */
  const handleFieldChange = (
    index: number,
    field: keyof CameraConfig,
    value: string
  ) => {
    const updatedCameras = cameras.map((cam, i) => {
      if (i === index) {
        return { ...cam, [field]: value };
      }
      return cam;
    });

    setCameras(updatedCameras);
    onConfigChange(updatedCameras);

    // Clear error for this field
    const errorKey = `${index}-${field}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  /**
   * Validate IP address format
   */
  const validateIP = (ip: string): boolean => {
    // Allow IP address or RTSP URL
    const ipRegex =
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const rtspRegex = /^rtsp:\/\/.+/i;
    return ipRegex.test(ip) || rtspRegex.test(ip);
  };

  /**
   * Validate on blur
   */
  const handleBlur = (index: number, field: keyof CameraConfig) => {
    const camera = cameras[index];
    const newErrors = { ...errors };

    if (field === "ip") {
      if (!camera.ip) {
        newErrors[`${index}-ip`] = "IP address is required";
      } else if (!validateIP(camera.ip)) {
        newErrors[`${index}-ip`] = "Invalid IP address or RTSP URL";
      } else {
        delete newErrors[`${index}-ip`];
      }
    }

    if (field === "location") {
      if (!camera.location) {
        newErrors[`${index}-location`] = "Location is required";
      } else {
        delete newErrors[`${index}-location`];
      }
    }

    setErrors(newErrors);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">
            Camera Configuration
          </h3>
          <p className="text-sm text-slate-400">
            Add up to {maxCameras} cameras to monitor
          </p>
        </div>
        <div className="text-sm text-slate-400">
          {cameras.length} / {maxCameras} cameras
        </div>
      </div>

      {/* Camera List */}
      <div className="space-y-4">
        {cameras.map((camera, index) => (
          <div
            key={camera.id}
            className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
          >
            {/* Camera Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📹</span>
                <span className="font-medium text-white">
                  Camera {index + 1}
                </span>
              </div>
              {cameras.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveCamera(index)}
                  className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
                >
                  Remove
                </button>
              )}
            </div>

            {/* Camera Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* IP Address */}
              <div>
                <Input
                  label="IP Address / RTSP URL *"
                  placeholder="192.168.1.101 or rtsp://..."
                  value={camera.ip}
                  onChange={(e) =>
                    handleFieldChange(index, "ip", e.target.value)
                  }
                  onBlur={() => handleBlur(index, "ip")}
                  error={errors[`${index}-ip`]}
                />
              </div>

              {/* Location */}
              <div>
                <Input
                  label="Location *"
                  placeholder="e.g., Main Entrance"
                  value={camera.location}
                  onChange={(e) =>
                    handleFieldChange(index, "location", e.target.value)
                  }
                  onBlur={() => handleBlur(index, "location")}
                  error={errors[`${index}-location`]}
                />
              </div>

              {/* Description - Full Width */}
              <div className="md:col-span-2">
                <Input
                  label="Description (optional)"
                  placeholder="e.g., Front door security camera"
                  value={camera.description || ""}
                  onChange={(e) =>
                    handleFieldChange(index, "description", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Camera Button */}
      {cameras.length < maxCameras && (
        <Button
          type="button"
          variant="ghost"
          onClick={handleAddCamera}
          className="w-full border-2 border-dashed border-slate-600 hover:border-cyan-500"
        >
          <span className="mr-2">+</span>
          Add Camera
        </Button>
      )}

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          <p className="text-red-400 text-sm">
            Please fix the errors above before submitting.
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Validate camera configuration
 */
export const validateCameraConfig = (
  cameras: CameraConfig[]
): { isValid: boolean; error?: string } => {
  if (cameras.length === 0) {
    return { isValid: false, error: "Please add at least one camera" };
  }

  for (let i = 0; i < cameras.length; i++) {
    const camera = cameras[i];

    if (!camera.ip || camera.ip.trim() === "") {
      return {
        isValid: false,
        error: `Camera ${i + 1}: IP address is required`,
      };
    }

    if (!camera.location || camera.location.trim() === "") {
      return { isValid: false, error: `Camera ${i + 1}: Location is required` };
    }
  }

  return { isValid: true };
};
